import React, { useRef, useState, useEffect } from 'react';
import { Navbar } from '@app/layouts/Navbar';
import HeroSection from '../components/home/HeroSection';
import StatsSection from '../components/home/StatsSection';
import FeaturesSection from '../components/home/FeaturesSection';
import TemplatesSection from '../components/home/TemplatesSection';
import DiariesSection from '../components/home/DiariesSection';
import DestinationsSection from '../components/home/DestinationsSection';
import EndingSection from '@shared/components/shared/EndingSection';
import StarField from '@shared/components/shared/StarFieldBg';
import OceanSurfaceGlow from '@shared/components/shared/OceanSurfaceBg';
import FloatingParticles from '@shared/components/shared/FloatingParticlesBg';
import FishLayer from '@shared/components/shared/FishLayerBg';
import LighthouseBeam from '@shared/components/shared/LighthouseBeamBg';

/**
 * Index — 西蒙的猫旅行首页
 * 
 * 深海动态背景层（固定视差）+ 内容层
 * 视差层带阻尼鼠标跟随
 */

const Index = () => {
  const containerRef = useRef(null);
  const layerRefs = useRef([]);

  // 鼠标视差 — 阻尼插值应用于多层
  useEffect(() => {
    if ('ontouchstart' in window) return;
    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    let lastTime = 0;
    let frame;

    const tick = (time) => {
      const dt = Math.min((time - lastTime) / 1000, 0.1);
      lastTime = time;
      const damping = 1 - Math.pow(0.05, dt);
      mouse.x += (mouse.tx - mouse.x) * damping;
      mouse.y += (mouse.ty - mouse.y) * damping;

      if (layerRefs.current[0]) layerRefs.current[0].style.transform = `translate(${mouse.x * -3}px, ${mouse.y * -2}px)`;
      if (layerRefs.current[1]) layerRefs.current[1].style.transform = `translate(${mouse.x * -6}px, ${mouse.y * -4}px)`;
      if (layerRefs.current[2]) layerRefs.current[2].style.transform = `translate(${mouse.x * -10}px, ${mouse.y * -7}px)`;
      if (layerRefs.current[3]) layerRefs.current[3].style.transform = `translate(${mouse.x * -14}px, ${mouse.y * -10}px)`;
      if (layerRefs.current[4]) layerRefs.current[4].style.transform = `translate(${mouse.x * -18}px, ${mouse.y * -13}px)`;

      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    const handler = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      mouse.tx = (e.clientX - rect.left) / rect.width - 0.5;
      mouse.ty = (e.clientY - rect.top) / rect.height - 0.5;
    };

    window.addEventListener('mousemove', handler);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('mousemove', handler);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative">
      {/* 固定深海动态背景层 */}
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none" style={{ background: '#06182A' }}>
        {/* Layer 0: 深海背景图 */}
        <div ref={el => layerRefs.current[0] = el} className="absolute inset-0 will-change-transform">
          <img
            src="/bg-deep-sea.png"
            alt=""
            className="absolute object-cover"
            style={{ top: '-3%', left: '-3%', width: '106%', height: '106%', filter: 'saturate(0.45) brightness(0.35) contrast(1.1)' }}
          />
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(180deg, rgba(6,18,34,0.4) 0%, rgba(6,18,34,0.2) 30%, rgba(6,18,34,0.3) 60%, rgba(6,18,34,0.6) 100%)',
          }} />
        </div>

        {/* Layer 1: 星空 */}
        <div ref={el => layerRefs.current[1] = el} className="absolute inset-0 will-change-transform">
          <StarField />
        </div>

        {/* Layer 2: 海面光影 */}
        <div ref={el => layerRefs.current[2] = el} className="absolute inset-0 will-change-transform">
          <OceanSurfaceGlow />
        </div>

        {/* Layer 3: 鱼群 */}
        <div ref={el => layerRefs.current[3] = el} className="absolute inset-0 will-change-transform">
          <FishLayer />
        </div>

        {/* Layer 4: 漂浮颗粒 */}
        <div ref={el => layerRefs.current[4] = el} className="absolute inset-0 will-change-transform">
          <FloatingParticles />
        </div>

        {/* 灯塔 */}
        <div className="absolute inset-0 z-[5]">
          <LighthouseBeam />
        </div>

        {/* 底部渐变 — 让内容可读 */}
        <div className="absolute inset-x-0 bottom-0 h-[60%] z-[6]" style={{
          background: 'linear-gradient(180deg, transparent 0%, rgba(6,18,34,0.5) 30%, rgba(6,18,34,0.95) 100%)',
        }} />
      </div>

      {/* 内容层（在动态背景之上） */}
      <div className="relative z-10">
        <Navbar />
        <main>
          <HeroSection />
          <StatsSection />
          <FeaturesSection />
          <TemplatesSection />
          <DiariesSection />
          <DestinationsSection />
        </main>
        <EndingSection />
      </div>
    </div>
  );
};

export default Index;
